<template>
    <div>
      <div class="dashboard-page-title">
        <div class="container-fluid">
          <h1>Booking Import</h1>
          <div class="spacer-b-2 w-25">
            <label for="type">Select an import type</label>
            <selectize
              id="type"
              class="selectize selectize-single"
              name="type"
              v-model="type"
              placeholder="Import Type"
              :options="typeOptions"/>
          </div>
          <p><a @click="downloadExample" href="#" target="_blank">Download an example file</a></p>
          <p>Expected date format : YYYY-MM-DD (e.g. 1990-12-31)<br/>
            Expected value separator inside a cell : | (e.g. john@test.com|doe@test.com)<br/>
            Expected opportunity reference format: 199 | 200</p>
        </div>
      </div>
      <div class="container-fluid">
        <div class="dashboard-block">
          <form>
            <div class="panel panel-grey" :class="{ 'is-droppable' : dropable }">
              <div class="panel-body">
                <div class="form-horizontal">
                  <div class="row">
                    <div class="col-md-12" v-if="isFailed">
                      <button class="btn btn-primary" type="button" @click="reset">Retry</button>
                    </div>
                    <div class="col-md-12 text-center" v-else-if="isSuccess">
                      <h3>{{ selectedLabel | sentenceCase }} file uploaded. New bookings will be reviewed and added to your
                        <router-link :to="'/' + selectedLink">list</router-link>.
                      </h3>
                      <button class="btn btn-primary" type="button" @click="reset">Upload another file</button>
                    </div>
                    <div class="col-md-12" v-else>
                      <div class="dropbox" @drop="fileDropped" @dragenter="dropable = true" @dragleave="dropable = false">
                        <input
                          v-if="!isSaving"
                          name="uploadFile"
                          :disabled="isSaving"
                          type="file"
                          @change="setFiles($event.target.files)"
                          class="form-control input-file"
                          accept="text/csv" />
                        <help-block field="file"/>
                        <p v-if="isSuccess">
                          File Uploaded
                        </p>
                        <p v-if="isInitial">
                          Click to browse <br />
                          Drag your file(s) here
                        </p>
                        <p v-if="isSaving">
                          Uploading files...
                        </p>
                        <p v-if="isFailed">
                          Failed to import
                        </p>
                        <span v-if="this.uploadFile">Upload File: {{this.uploadFile.name}}</span>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      <validate-input :file-error="fileError" :importer-errors="importerErrors" :importer-warnings="[]"></validate-input>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div class="dashboard-page-title">
        <div class="container-fluid">
          <button
            type="button"
            id="formSubmit"
            name="formSubmit"
            class="btn btn-primary"
            @click="uploadFiles"
          >Start upload</button>
        </div>
      </div>
    </div>
</template>

<script>
import ValidateInput from './ValidateInput'
import Selectize from '../ext/codium/CodiumSelectize'
import { notify } from 'utils'
const STATUS_INITIAL = 0
const STATUS_SAVING = 1
const STATUS_SUCCESS = 2
const STATUS_FAILED = 3

export default {
  name: 'Importer',
  components: {
    ValidateInput,
    Selectize
  },
  data () {
    return {
      uploadedFiles: [],
      uploadError: null,
      currentStatus: null,
      importerErrors: [],
      fileError: null,
      type: 'completed_bookings',
      uploadFile: null,
      dropable: false
    }
  },
  computed: {
    typeOptions () {
      const options = [
      ]
      if (this.$userIsAdmin) {
        options.push({ id: 'completed_bookings', text: 'Completed Bookings', link: 'bookings' })
      }
      return options
    },
    selectedLabel () {
      return this.typeOptions.find(element => element.id === this.type).text
    },
    selectedLink () {
      return this.typeOptions.find(element => element.id === this.type).link
    },
    isInitial () {
      return this.currentStatus === STATUS_INITIAL
    },
    isSaving () {
      return this.currentStatus === STATUS_SAVING
    },
    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },
    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  methods: {
    reset () {
      // reset form to initial state
      this.currentStatus = STATUS_INITIAL
      this.uploadedFiles = []
      this.uploadFile = null
      this.uploadError = null
      this.importerErrors = []
      this.fileError = null
    },
    save (formData) {
      // upload data to the server
      this.currentStatus = STATUS_SAVING
      this.$http.post(`booking-import/${this.type}`, formData)
        .then((response) => {
          this.uploadedFiles = [].concat(response)
          this.currentStatus = STATUS_SUCCESS
          notify.success('Your file has been uploaded and will be processed shortly')
          this.importerErrors = []
          this.fileError = null
        })
        .catch(err => {
          notify.error('There are issues with your import. Please review and try again')
          this.currentStatus = STATUS_FAILED
          this.handleImportErrorsCallback(err)
        })
    },
    handleImportErrorsCallback (error) {
      if (error.response.status === 422) {
        this.importerErrors = error.response.data
        this.uploadError = error.response
      } else if (error.response.data.message) {
        this.fileError = error.response.data.message
      }
    },
    fileDropped (e) {
      this.dropable = false

      if (!e.dataTransfer.files || !e.dataTransfer.files.length) {
        return
      }

      this.setFiles(e.dataTransfer.files)
    },
    setFiles (files) {
      if (files.length !== 0) {
        for (let i = 0; i < files.length; i++) {
          if (files[i].size >= 30000000) {
            notify.error('Maximum file size is 30MB. Unable to upload file: ' + files[i].name)
          } else {
            this.uploadFile = files[i]
          }
        }
      }
    },
    uploadFiles () {
      // handle file changes
      const formData = new FormData()

      if (!this.uploadFile) return

      // append the files to FormData
      formData.append('uploadFile', this.uploadFile)

      // save it
      this.save(formData)
    },
    downloadExample (e) {
      e.preventDefault()
      this.$http.get(`booking-import/example/${this.type}`)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data], {type: 'text/csv'}))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `example-${this.type}-import.csv`)
          document.body.appendChild(link)
          link.click()
        })
        .catch(hr => {
          notify.error('There are issues with your import. Please review and try again')
        })
    }
  },
  mounted () {
    this.reset()
  }
}
</script>

<style lang="scss">
  .dashboard-block {
    .panel-grey {
      &.is-droppable {
        background-color: #bdbdbd;
      }
    }
  }

  .dropbox {
    outline: 2px dashed grey; /* the dash box */
    outline-offset: -10px;
    background: lightcyan;
    color: dimgray;
    padding: 10px 10px;
    min-height: 50px; /* minimum height */
    position: relative;
    cursor: pointer;
  }

  .input-file {
    opacity: 0; /* invisible but it's there! */
    width: 100%;
    height: 200px;
    position: absolute;
    cursor: pointer;
  }

  .dropbox:hover {
    background: lightblue; /* when mouse over to the drop zone, change color */
  }

  .dropbox p {
    font-size: 1.2em;
    text-align: center;
    padding: 50px 0;
  }

  .dropbox span {
    display: block;
    text-align: center;
    padding: 3px 0;
  }

</style>
